import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Select, DateSelect, Toggle } from 'components/FormFields';
import Input from 'components/FormFields/Input';
import SearchableSelect from 'components/FormFields/SearchableSelect';
import { useGetSeriesAdmin } from 'components/SeriesSelect/useGetSeriesAdmin';
import Toaster from 'components/Toaster';
import { useAccountSearch } from '../CreateEventModal/hooks/useAccountSearch';
import { useCreateEventV3 } from '../CreateEventModal/hooks/useCreateEventV3';
import { useTrackSearch } from '../CreateEventModal/hooks/useTrackSearch';

// Query to fetch seating charts
const GET_SEATING_CHARTS = gql`
  query GetSeatingCharts {
    getSeatingCharts {
      id
      name
    }
  }
`;

// Page wrapper - ensures full viewport coverage with theme background
const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) =>
    theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'};
`;

// Page container
const Container = styled.div`
  padding: 30px;
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

// Page header
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

// Date display card - matches design system (same as EditEventV2)
const DateDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fa4616;
  color: white;
  border-radius: 8px;
  padding: 10px 16px;
  min-width: 70px;
  gap: 4px;
`;

const DatePill = styled.div`
  background: white;
  color: #1e293b;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  letter-spacing: 0.5px;
`;

const DateText = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 4px 0;
  line-height: 1;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

// Button wrapper to control width for header buttons
const HeaderButton = styled.div`
  width: auto;

  button {
    width: auto;
    padding: 10px 24px;
  }
`;

// Form styles
const FormContent = styled.div`
  padding: 24px;
`;

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1f2937')};
  margin: 0 0 20px 0;
`;

const FormRow = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormRowGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleStack = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 12px;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9'};
    border-color: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.12)'};
  }
`;

const ToggleIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(250, 70, 22, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
    color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#fa4616')};
  }
`;

const ToggleContent = styled.div`
  flex: 1;
`;

const ToggleTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1f2937')};
  margin-bottom: 4px;
`;

const ToggleDescription = styled.div`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.4;
`;

const ExpandedSection = styled.div`
  margin-top: 16px;
  padding: 20px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f8fafc'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
`;

const InfoBox = styled.div`
  padding: 12px 16px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'};
  border-radius: 6px;
  margin-bottom: 16px;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${(props) => (props.theme.name === 'dark' ? '#93c5fd' : '#1e40af')};
`;

const SectionLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#e5e7eb' : '#374151')};
  margin-bottom: 8px;
`;

const OptionalBadge = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${(props) => (props.theme.name === 'dark' ? '#6b7280' : '#9ca3af')};
  margin-left: 6px;
`;

// Duration options for event length
const DURATION_OPTIONS = [
  { value: 1, label: '1 Day' },
  { value: 2, label: '2 Days' },
  { value: 3, label: '3 Days' },
  { value: 4, label: '4 Days' },
  { value: 5, label: '5 Days' },
  { value: 6, label: '6 Days' },
  { value: 7, label: '7 Days' },
];

// Helper to parse date for display in DateBox
const parseDateForDisplay = (startDateString, _endDateString, duration) => {
  if (!startDateString) return null;

  // Parse MM-DD-YYYY format
  const [month, day, year] = startDateString.split('-').map(Number);
  if (!month || !day || !year) return null;

  const startDate = new Date(year, month - 1, day);
  if (isNaN(startDate.getTime())) return null;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Check if multi-day event
  const isMultiDay = duration > 1;
  let endDay = null;

  if (isMultiDay) {
    const endDate = new Date(year, month - 1, day + (duration - 1));
    endDay = endDate.getDate();
  }

  return {
    month: months[startDate.getMonth()],
    day: startDate.getDate(),
    dayOfWeek: days[startDate.getDay()],
    year: startDate.getFullYear(),
    isMultiDay,
    endDay,
  };
};

// Calculate end date from start date and duration
const calculateEndDate = (startDate, duration) => {
  if (!startDate || duration === 1) return startDate;

  // Parse MM-DD-YYYY format
  const [month, day, year] = startDate.split('-').map(Number);
  const start = new Date(year, month - 1, day);

  // Add (duration - 1) days
  start.setDate(start.getDate() + (duration - 1));

  // Format back to MM-DD-YYYY
  const endMonth = String(start.getMonth() + 1).padStart(2, '0');
  const endDay = String(start.getDate()).padStart(2, '0');
  const endYear = start.getFullYear();

  return `${endMonth}-${endDay}-${endYear}`;
};

// Inner component that uses theme
const CreateEventContent = () => {
  const history = useHistory();
  const location = useLocation();

  // Determine route prefix for context-aware navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const [formState, setFormState] = useState({
    name: '',
    start_date: '',
    duration: 1,
    track_id: null,
    account_id: location.state?.accountId || null,
    has_sales_windows: false,
    has_reserved_seating: false,
    seating_chart_id: null,
    series_ids: [],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });

  const { tracks, loading: tracksLoading } = useTrackSearch();
  const { accounts, loading: accountsLoading } = useAccountSearch();
  const createEventV3 = useCreateEventV3();
  const { data: seriesData, loading: seriesLoading } = useGetSeriesAdmin();
  const seriesOptions = (seriesData?.getSeriesAdmin?.results || []).map(
    (s) => ({
      value: s.id,
      label: s.name,
    })
  );

  // Fetch seating charts
  const { data: chartsData, loading: chartsLoading } =
    useQuery(GET_SEATING_CHARTS);
  const seatingChartOptions = (chartsData?.getSeatingCharts || []).map(
    (chart) => ({
      value: chart.id,
      label: chart.name,
    })
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formState.name?.trim()) {
      newErrors.name = 'Event name is required';
    }
    if (!formState.start_date) {
      newErrors.start_date = 'Start date is required';
    }
    if (!formState.track_id) {
      newErrors.track_id = 'Track is required';
    }
    if (!formState.account_id) {
      newErrors.account_id = 'Account is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    history.push(`${routePrefix}/events`);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const endDate = calculateEndDate(
        formState.start_date,
        formState.duration
      );
      const response = await createEventV3({
        name: formState.name,
        start_date: formState.start_date,
        end_date: endDate,
        track_id: Number(formState.track_id),
        account_id: formState.account_id ? Number(formState.account_id) : null,
        sales_windows: formState.has_sales_windows,
        reserved_seats: formState.has_reserved_seating,
        chart_id: formState.seating_chart_id || null,
        series_ids: formState.series_ids.length
          ? formState.series_ids.map(Number)
          : null,
      });

      if (response?.data?.createEventV3?.id) {
        setToaster({
          isOpen: true,
          type: 'success',
          title: 'Success!',
          message: 'Event created successfully.',
        });
        // Redirect to tickets step after short delay so user sees success message
        setTimeout(() => {
          history.push(
            `${routePrefix}/events/addEvent/${response.data.createEventV3.id}/tickets`
          );
        }, 500);
      }
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to create event. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDateChange = (value) => {
    // DateSelect already outputs MM-DD-YYYY format
    setFormState((prev) => ({ ...prev, start_date: value }));
    if (errors.start_date && value) {
      setErrors((prev) => ({ ...prev, start_date: undefined }));
    }
  };

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <HeaderLeft>
            {(() => {
              const dateInfo = parseDateForDisplay(
                formState.start_date,
                null,
                formState.duration
              );
              if (!dateInfo) return null;
              return (
                <DateDisplay>
                  {dateInfo.isMultiDay ? (
                    <>
                      <DatePill>{dateInfo.month}</DatePill>
                      <DateText>
                        {dateInfo.day} - {dateInfo.endDay}
                      </DateText>
                    </>
                  ) : (
                    <>
                      <DatePill>{dateInfo.dayOfWeek}</DatePill>
                      <DateText>
                        {dateInfo.month} {dateInfo.day}
                      </DateText>
                    </>
                  )}
                </DateDisplay>
              );
            })()}
            <EventInfo>
              <Title>Create Event</Title>
              <Subtitle>Add a new event with essential details</Subtitle>
            </EventInfo>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButton>
              <ButtonV2 type="button" onClick={handleCancel}>
                Cancel
              </ButtonV2>
            </HeaderButton>
            <HeaderButton>
              <ButtonV2
                elevated
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Event'}
              </ButtonV2>
            </HeaderButton>
          </HeaderRight>
        </PageHeader>

        {/* Card 1: Track and Account */}
        <Card>
          <FormContent>
            <CardTitle>Where is your event?</CardTitle>
            <FormRowGrid>
              <SearchableSelect
                label="Track"
                required
                value={formState.track_id}
                onChange={(option) => {
                  setFormState((prev) => ({
                    ...prev,
                    track_id: option?.value || null,
                  }));
                  if (errors.track_id) {
                    setErrors((prev) => ({ ...prev, track_id: undefined }));
                  }
                }}
                options={tracks}
                loading={tracksLoading}
                placeholder="Search tracks..."
                error={!!errors.track_id}
                helperText={errors.track_id}
              />
              <SearchableSelect
                label="Account"
                required
                value={formState.account_id}
                onChange={(option) => {
                  setFormState((prev) => ({
                    ...prev,
                    account_id: option?.value || null,
                  }));
                  if (errors.account_id) {
                    setErrors((prev) => ({ ...prev, account_id: undefined }));
                  }
                }}
                options={accounts}
                loading={accountsLoading}
                placeholder="Search accounts..."
                error={!!errors.account_id}
                helperText={errors.account_id}
              />
            </FormRowGrid>
            <FormRow>
              <SearchableSelect
                label="Series"
                placeholder="Search and add series..."
                options={seriesOptions.filter(
                  (s) => !formState.series_ids.includes(s.value)
                )}
                loading={seriesLoading}
                onChange={(option) => {
                  if (!option?.value) return;
                  if (!formState.series_ids.includes(option.value)) {
                    setFormState((prev) => ({
                      ...prev,
                      series_ids: [...prev.series_ids, option.value],
                    }));
                  }
                }}
                value={null}
                key={`series-select-${formState.series_ids.length}`}
              />
              {formState.series_ids.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginTop: '8px',
                  }}
                >
                  {formState.series_ids.map((seriesId) => {
                    const series = seriesOptions.find(
                      (s) => s.value === seriesId
                    );
                    return (
                      <span
                        key={seriesId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '16px',
                          fontSize: '13px',
                          fontWeight: 500,
                          background: 'rgba(250, 70, 22, 0.1)',
                          color: '#fa4616',
                        }}
                      >
                        {series?.label || `Series ${seriesId}`}
                        <button
                          type="button"
                          onClick={() =>
                            setFormState((prev) => ({
                              ...prev,
                              series_ids: prev.series_ids.filter(
                                (id) => id !== seriesId
                              ),
                            }))
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            color: 'inherit',
                            fontSize: '16px',
                            lineHeight: 1,
                          }}
                          aria-label="Remove series"
                        >
                          &times;
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </FormRow>
          </FormContent>
        </Card>

        {/* Card 2: Date fields */}
        <Card style={{ marginTop: '20px' }}>
          <FormContent>
            <CardTitle>When is your event?</CardTitle>
            <FormRowGrid>
              <DateSelect
                label="Start Date"
                required
                value={formState.start_date}
                onChange={handleDateChange}
                error={!!errors.start_date}
                helperText={errors.start_date}
                showModeToggle
              />

              <Select
                label="Duration"
                required
                value={formState.duration}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setFormState((prev) => ({
                    ...prev,
                    duration: value,
                  }));
                }}
                options={DURATION_OPTIONS}
              />
            </FormRowGrid>
          </FormContent>
        </Card>

        {/* Card 3: Event Name */}
        <Card style={{ marginTop: '20px' }}>
          <FormContent>
            <CardTitle>Tell us about your event</CardTitle>
            <FormRow>
              <Input
                label="Event Name"
                required
                value={formState.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormState((prev) => ({ ...prev, name: value }));
                  if (errors.name) {
                    setErrors((prev) => ({ ...prev, name: undefined }));
                  }
                }}
                placeholder="Enter event name"
                error={!!errors.name}
                helperText={errors.name}
              />
            </FormRow>

            <ToggleStack>
              <ToggleCard>
                <ToggleIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </ToggleIcon>
                <ToggleContent>
                  <ToggleTitle>Will you sell reserved items?</ToggleTitle>
                  <ToggleDescription>
                    Let customers choose specific seats, camping spots, or
                    parking spaces from an interactive map
                  </ToggleDescription>
                </ToggleContent>
                <Toggle
                  checked={formState.has_reserved_seating}
                  onChange={() => {
                    setFormState((prev) => ({
                      ...prev,
                      has_reserved_seating: !prev.has_reserved_seating,
                      // Clear seating chart when disabling
                      seating_chart_id: !prev.has_reserved_seating
                        ? prev.seating_chart_id
                        : null,
                    }));
                  }}
                />
              </ToggleCard>

              <ToggleCard>
                <ToggleIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </ToggleIcon>
                <ToggleContent>
                  <ToggleTitle>Will you offer promotional pricing?</ToggleTitle>
                  <ToggleDescription>
                    Create Early Bird, Last Chance, or other time-based pricing
                    tiers for your tickets
                  </ToggleDescription>
                </ToggleContent>
                <Toggle
                  checked={formState.has_sales_windows}
                  onChange={() => {
                    setFormState((prev) => ({
                      ...prev,
                      has_sales_windows: !prev.has_sales_windows,
                    }));
                  }}
                />
              </ToggleCard>
            </ToggleStack>

            {/* Expanded section when Reserved Seating is enabled */}
            {formState.has_reserved_seating && (
              <ExpandedSection>
                <InfoBox>
                  <InfoText>
                    You can set a default seating chart here for all reserved
                    tickets, or leave it empty and assign different charts to
                    individual tickets later. This is useful if you have
                    multiple seating areas (e.g., grandstand seats vs. camping
                    spots) that use different charts.
                  </InfoText>
                </InfoBox>

                <SectionLabel>
                  Event Seating Chart
                  <OptionalBadge>(Optional)</OptionalBadge>
                </SectionLabel>
                <SearchableSelect
                  value={formState.seating_chart_id}
                  onChange={(option) => {
                    setFormState((prev) => ({
                      ...prev,
                      seating_chart_id: option?.value || null,
                    }));
                  }}
                  options={seatingChartOptions}
                  loading={chartsLoading}
                  placeholder="Search seating charts..."
                  isClearable
                />
              </ExpandedSection>
            )}
          </FormContent>
        </Card>
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

// Main component wrapped with theme provider
const CreateEventV2 = () => {
  return (
    <EnhancedThemeProvider>
      <CreateEventContent />
    </EnhancedThemeProvider>
  );
};

export default CreateEventV2;
